import { Avatar, Button, Input } from "@mantine/core";
import type {
  ColumnsType,
  SorterResult,
  TablePaginationConfig,
} from "antd/es/table/interface";
import { DatePicker, Table } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import DepartmentCard from "@/components/DepartmentCard";
import type { FilterValue } from "antd/es/table/interface";
import dayjs from "dayjs";
import { orpcClient } from "@/lib/orpc-client";
import { useTranslation } from "react-i18next";

interface QuotationData {
  id: string;
  name: string;
  date: string;
  partner: {
    name: string;
  };
  remarks: string;
  partnerAddress: {
    city: string;
    district: string;
    postalCode: string;
  };
}

function QuotationManagement({
  status = "normal",
}: {
  /** 要顯示的專案狀態；close 代表已封存清單 */
  status?: "normal" | "close";
}) {
  const { t } = useTranslation();
  const isArchived = status === "close";
  // list or grid
  const [layout, setLayout] = useState("list");
  const [quotationData, setQuotationData] = useState<QuotationData[]>([]);
  const [filteredData, setFilteredData] = useState<QuotationData[]>([]);
  const [sortedInfo, setSortedInfo] = useState<SorterResult<QuotationData>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    name: "",
    date: "",
    partner: "",
  });

  console.log("filteredData", filteredData);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleQuotationData = () => {
    navigate("/hrMainPage/addProjectQuotation");
  };

  const getFiltersFromURL = () => {
    return {
      name: searchParams.get("name") || "",
      date: searchParams.get("date") || "",
      partner: searchParams.get("partner") || "",
    };
  };

  const getCurrentPageFromURL = () => {
    const page = searchParams.get("page");
    return page ? parseInt(page, 10) : 1;
  };

  const getPageSizeFromURL = () => {
    const size = searchParams.get("pageSize");
    return size ? parseInt(size, 10) : 10;
  };

  const updateURL = (newFilters?: any, page?: number, size?: number) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters !== undefined) {
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== "") {
          params.set(key, value as string);
        } else {
          params.delete(key);
        }
      });
    }

    if (page !== undefined) {
      if (page > 1) {
        params.set("page", page.toString());
      } else {
        params.delete("page");
      }
    }

    if (size !== undefined && size !== 10) {
      params.set("pageSize", size.toString());
    } else if (size === 10) {
      params.delete("pageSize");
    }

    setSearchParams(params);
  };

  const handleLayoutChange = (newLayout: string) => {
    setLayout(newLayout);
  };

  const fetchData = async () => {
    try {
      setLoading(true);

      const result = await orpcClient.project.list({ filter: { status } });

      setQuotationData(result as any);
      applyLocalFilters(result as any, filters);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const applySorting = (data: QuotationData[]) => {
    if (!sortedInfo.columnKey || !sortedInfo.order) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const columnKey = sortedInfo.columnKey as string;
      let aValue: any;
      let bValue: any;

      if (columnKey === "partner") {
        aValue = a.partner?.name || "";
        bValue = b.partner?.name || "";
      } else if (columnKey === "date") {
        aValue = a.date ? dayjs(a.date).valueOf() : 0;
        bValue = b.date ? dayjs(b.date).valueOf() : 0;
      } else {
        aValue = a[columnKey as keyof QuotationData] || "";
        bValue = b[columnKey as keyof QuotationData] || "";
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortedInfo.order === "ascend" ? comparison : -comparison;
      }

      if (aValue < bValue) {
        return sortedInfo.order === "ascend" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortedInfo.order === "ascend" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const applyLocalFilters = (
    data: QuotationData[],
    currentFilters: typeof filters
  ) => {
    let filtered = [...data];

    if (currentFilters.name) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(currentFilters.name.toLowerCase())
      );
    }

    if (currentFilters.date) {
      filtered = filtered.filter((item) => {
        const itemDate = dayjs(item.date).format("YYYY-MM-DD");
        return itemDate === currentFilters.date;
      });
    }

    if (currentFilters.partner) {
      filtered = filtered.filter((item) =>
        item.partner?.name
          ?.toLowerCase()
          .includes(currentFilters.partner.toLowerCase())
      );
    }

    const sorted = applySorting(filtered);
    setFilteredData(sorted);
    setTotalItems(sorted.length);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<QuotationData> | SorterResult<QuotationData>[]
  ) => {
    if (
      pagination.current !== currentPage ||
      pagination.pageSize !== pageSize
    ) {
      setCurrentPage(pagination.current || 1);
      setPageSize(pagination.pageSize || 10);
      updateURL(undefined, pagination.current, pagination.pageSize);
    }

    if (!Array.isArray(sorter)) {
      setSortedInfo(sorter);
    }
  };

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = {
      ...filters,
      [field]: value,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters, 1);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      applyLocalFilters(quotationData, newFilters);
    }, 500);
  };

  const handleDateChange = (date: any, dateString: string | string[]) => {
    const dateValue = Array.isArray(dateString) ? dateString[0] : dateString;
    const newFilters = {
      ...filters,
      date: dateValue,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    updateURL(newFilters, 1);
    applyLocalFilters(quotationData, newFilters);
  };

  const clearFilters = () => {
    const clearedFilters = {
      name: "",
      date: "",
      partner: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    updateURL(clearedFilters, 1);
    applyLocalFilters(quotationData, clearedFilters);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    updateURL(filters, 1);
    applyLocalFilters(quotationData, filters);
  };

  const columns: ColumnsType<QuotationData> = [
    {
      title: t("quotation.name") || "名稱",
      dataIndex: "name",
      key: "name",
      fixed: "left" as const,
      sorter: true,
      sortOrder: sortedInfo.columnKey === "name" ? sortedInfo.order : null,
      render: (text: string) => text || "",
    },
    {
      title: t("quotation.date") || "日期",
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "date" ? sortedInfo.order : null,
      render: (text: string) => {
        console.log("text", text);
        return dayjs(text).format("YYYY-MM-DD") || "";
      },
    },
    {
      title: t("quotation.customer") || "合作方（客戶）",
      dataIndex: "partner",
      key: "partner",
      sorter: true,
      sortOrder: sortedInfo.columnKey === "partner" ? sortedInfo.order : null,
      render: (partner: { name: string }) => partner?.name || "",
    },
    {
      title: t("quotation.remarks") || "備註",
      dataIndex: "remarks",
      key: "remarks",
      sorter: false,
      sortOrder: sortedInfo.columnKey === "remarks" ? sortedInfo.order : null,
      render: (text: string) => text || "",
    },
    {
      title: t("quotation.address") || "地址",
      dataIndex: "partnerAddress",
      key: "partnerAddress",
      sorter: false,
      sortOrder:
        sortedInfo.columnKey === "partnerAddress" ? sortedInfo.order : null,
      render: (address: {
        city: string;
        district: string;
        postalCode: string;
      }) =>
        `${address?.city || ""}${address?.district || ""}${address?.postalCode || ""}`,
    },
  ];

  useEffect(() => {
    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    setFilters(urlFilters);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);

    fetchData();
    // status 改變代表切換到另一個清單（專案列表 / 已封存），需要重新抓資料
  }, [status]);

  useEffect(() => {
    if (quotationData.length > 0) {
      applyLocalFilters(quotationData, filters);
    }
  }, [sortedInfo]);

  useEffect(() => {
    if (quotationData.length === 0) return;

    const urlFilters = getFiltersFromURL();
    const urlPage = getCurrentPageFromURL();
    const urlPageSize = getPageSizeFromURL();

    if (
      JSON.stringify(urlFilters) !== JSON.stringify(filters) ||
      urlPage !== currentPage ||
      urlPageSize !== pageSize
    ) {
      setFilters(urlFilters);
      setCurrentPage(urlPage);
      setPageSize(urlPageSize);
      applyLocalFilters(quotationData, urlFilters);
    }
  }, [searchParams]);

  useEffect(() => {
    const handlePaginationInput = () => {
      const paginationInputs = document.querySelectorAll(
        ".ant-pagination-options .ant-select input"
      );
      paginationInputs.forEach((input) => {
        const inputElement = input as HTMLInputElement;
        inputElement.readOnly = true;
        inputElement.style.cursor = "pointer";
        inputElement.style.pointerEvents = "none";
        inputElement.style.caretColor = "transparent";
        inputElement.style.userSelect = "none";
      });

      const selectors = document.querySelectorAll(
        ".ant-pagination-options .ant-select .ant-select-selector"
      );
      selectors.forEach((selector) => {
        (selector as HTMLElement).style.cursor = "pointer";
      });
    };

    const observer = new MutationObserver(handlePaginationInput);
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(handlePaginationInput, 100);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
      // Clean up debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="">
      <div
        className="flex items-center justify-between px-4"
        style={{ backgroundColor: "#ECECEC" }}
      >
        <div></div>
        {!isArchived && (
          <Button
            variant="default"
            style={{ minWidth: "3rem", margin: "1rem" }}
            radius="xl"
            onClick={handleQuotationData}
          >
            {t("quotation.addProject") || "新增專案"}
          </Button>
        )}
      </div>

      {/* Filter Section */}
      <div
        className="flex items-center gap-4 px-4 py-3 mb-4"
        style={{ backgroundColor: "#F5F5F5", borderRadius: "8px" }}
      >
        <div className="flex items-center gap-2">
          <span style={{ minWidth: "60px", fontWeight: "500" }}>
            {t("quotation.name") || "名稱"}:
          </span>
          <Input
            placeholder={t("quotation.searchName") || "搜尋名稱"}
            value={filters.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFilterChange("name", e.target.value)
            }
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex items-center gap-2">
          <span style={{ minWidth: "60px", fontWeight: "500" }}>
            {t("quotation.date") || "日期"}:
          </span>
          <DatePicker
            placeholder={t("quotation.selectDate") || "選擇日期"}
            value={filters.date ? dayjs(filters.date) : null}
            onChange={handleDateChange}
            style={{ width: "150px" }}
            format="YYYY-MM-DD"
          />
        </div>

        <div className="flex items-center gap-2">
          <span style={{ minWidth: "60px", fontWeight: "500" }}>
            {t("quotation.partner") || "合作方"}:
          </span>
          <Input
            placeholder={t("quotation.searchPartner") || "搜尋合作方"}
            value={filters.partner}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleFilterChange("partner", e.target.value)
            }
            style={{ width: "150px" }}
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={applyFilters} variant="filled" size="sm">
            {t("quotation.search") || "搜尋"}
          </Button>
          <Button onClick={clearFilters} variant="default" size="sm">
            {t("quotation.clear") || "清除"}
          </Button>
        </div>
      </div>
      {/* <div className="flex justify-end p-8">
        <IconList
          onClick={() => handleLayoutChange("list")}
          className="mr-2 cursor-pointer"
          size={30}
        />
        <IconLayoutGridFilled
          onClick={() => handleLayoutChange("grid")}
          className="mr-2 cursor-pointer"
          size={30}
        />
      </div> */}
      {layout === "list" ? (
        <div className="p-4">
          <div
            className="relative"
            style={
              {
                "--ant-pagination-input-pointer-events": "none",
                "--ant-pagination-input-cursor": "pointer",
                "--ant-pagination-input-caret": "transparent",
              } as React.CSSProperties
            }
          >
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey="id"
              onChange={handleTableChange}
              onRow={(record) => ({
                onClick: () =>
                  navigate(`/hrMainPage/projectQuotation/${record.id}`),
                style: { cursor: "pointer" },
              })}
              scroll={{ x: "max-content", y: 600 }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: totalItems,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showQuickJumper: false,
              }}
              loading={loading}
              locale={
                isArchived
                  ? {
                      emptyText:
                        t("quotation.noArchivedProjects") || "沒有已封存的專案",
                    }
                  : undefined
              }
              bordered
              sticky
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {filteredData.map((data) => (
            <DepartmentCard
              key={data.id}
              props={data}
              onClick={() =>
                navigate(`/hrMainPage/projectQuotation/${data.id}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default QuotationManagement;
